import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, Modal } from "react-bootstrap";
import { PieChart, Pie, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Briefcase, Calendar, Star, Eye, TrendingUp, TrendingDown, MapPin, Video, CheckCircle2, XCircle, Info, Users, Film } from "lucide-react";
import Swal from 'sweetalert2';
import { fetchTalentDashboardApi, followUserApi } from "../../../services/api";
import ModernAuditionCard from "../../../components/ModernAuditionCard";
import "./TalentDashboard.css";

const TalentDashboard = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatCamelCase = (text) => {
    if (!text) return "Casting Director";
    return String(text).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ").replace(/\s+/g, " ").trim().replace(/\b\w/g, char => char.toUpperCase());
  };

  const navigateToCDProfile = (cdId) => {
    setShowFollowingModal(false);
    navigate(`/dashboard/casting-director-profile/${cdId}`);
  };

  const handleModalFollowToggle = async (cdId, cdName) => {
    try {
      const res = await followUserApi(cdId);
      if (res.data?.success || res.success) {
        const isNowFollowed = res.data?.followed ?? res.followed;
        setDashboardData((prev) => {
          if (!prev) return prev;
          let updatedList = (prev.followedCDs || []).filter(item => item.id !== cdId);
          return { ...prev, kpis: { ...prev.kpis, followingCount: updatedList.length }, followedCDs: updatedList };
        });
        Swal.fire({ icon: 'success', title: 'Unfollowed', text: `You have unfollowed ${cdName}.`, width: '300px', background: '#131824', color: '#ffffff', timer: 1500, showConfirmButton: false });
      }
    } catch (err) {
      console.error("Follow toggle error:", err);
    }
  };

  const currentInnerRadius = isMobile ? 50 : 75;
  const currentOuterRadius = isMobile ? 70 : 100;

  const mockKpis = [
    { title: "Total Applications", value: dashboardData?.kpis?.totalApplications || 0, icon: <Briefcase />, trend: "+12%", isPositive: true },
    { title: "Pending", value: dashboardData?.kpis?.pendingCount || 0, icon: <Calendar />, trend: "+5%", isPositive: true },
    { title: "Shortlisted", value: dashboardData?.kpis?.shortlistedCount || 0, icon: <CheckCircle2 />, trend: "+2%", isPositive: true },
    { title: "Rejected", value: dashboardData?.kpis?.rejectedCount || 0, icon: <XCircle />, trend: "0%", isPositive: false }
  ];

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetchTalentDashboardApi();
      setDashboardData(response.data.data);
    } catch (error) { console.log(error); } finally { setLoading(false); }
  };

  const getStatusBadge = (status) => {
    const s = status.toLowerCase();
    let color = "#9da3b0", bgColor = "rgba(157, 163, 176, 0.1)", text = status;
    if (s === "applied" || s === "pending") { color = "#ffb020"; bgColor = "rgba(255, 176, 32, 0.12)"; text = "Pending"; }
    else if (s === "reviewed") { color = "#06b6d4"; bgColor = "rgba(6, 182, 212, 0.12)"; text = "Reviewed"; }
    else if (s === "scheduled") { color = "#3b82f6"; bgColor = "rgba(59, 130, 246, 0.12)"; text = "Scheduled"; }
    else if (s === "selected" || s === "shortlisted") { color = "#00c781"; bgColor = "rgba(0, 199, 129, 0.12)"; text = "Shortlisted"; }
    else if (s === "rejected" || s === "resubmit") { color = "#ff4d4f"; bgColor = "rgba(255, 77, 79, 0.12)"; text = s === "resubmit" ? "Resubmit" : "Rejected"; }
    return <span className="fw-bold px-3 py-1.5" style={{ fontSize: '0.75rem', borderRadius: '4px', letterSpacing: '0.5px', color, backgroundColor: bgColor, display: 'inline-block', border: `1px solid ${color}26` }}>{text.toUpperCase()}</span>;
  };

  const pieChartData = (dashboardData?.chartData || []).filter(item => item.name.toLowerCase() !== "total");
  const COLOR_MAP = { Shortlisted: "#00c781", Pending: "#ffb020", Rejected: "#ff4d4f", Applications: "#E10600" };
  const totalAppliedCount = dashboardData?.kpis?.totalApplications || 0;

  if (loading) {
    return (
      <div className="text-center p-5 text-white" style={{ minHeight: "80vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <Spinner animation="border" variant="danger" />
        <p className="mt-3">Loading Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="td-wrapper">
      <div className="td-container">
        
        {/* HEADER */}
        <div className="td-header d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h1 className="td-title">My Dashboard</h1>
            <p className="td-subtitle">Welcome back! Track your auditions and find your next big role.</p>
          </div>
          
          {/* ✅ DESKTOP TOP-RIGHT HEADER TILES WRAPPER */}
          <div className="d-flex align-items-center gap-3 td-header-tiles-wrapper">
            
            {/* FOLLOWING TILE */}
            <div className="td-following-tile" onClick={() => setShowFollowingModal(true)} style={{ cursor: "pointer", background: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.1)", padding: "10px 16px", borderRadius: "12px", display: "flex", alignItems: "center", gap: "12px", backdropFilter: "blur(10px)", transition: "all 0.3s ease" }} title="Click to view followed Casting Directors">
              <div className="following-tile-icon-box" style={{ width: "32px", height: "32px", borderRadius: "8px", background: "rgba(59, 130, 246, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Users size={16} color="#3b82f6" />
              </div>
              <div className="following-tile-info" style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                <span className="following-tile-label text-truncate" style={{ fontSize: "10px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.8px" }}>Following</span>
                <span className="following-tile-count text-truncate" style={{ fontSize: "13px", fontWeight: "700", color: "#ffffff" }}>{dashboardData?.kpis?.followingCount || 0} CDs</span>
              </div>
            </div>

            {/* PROFILE SCORE TILE */}
            <div className="td-profile-score-tile" onClick={() => navigate("/dashboard/profile/edit")} style={{ cursor: "pointer" }} title="Click to edit your profile">
              <div className="score-tile-icon-box"><Star size={16} fill="var(--td-accent-yellow)" color="var(--td-accent-yellow)" /></div>
              <div className="score-tile-info" style={{ minWidth: 0 }}>
                <span className="score-tile-label text-truncate">Profile Score</span>
                <div className="score-tile-progress-wrapper">
                  <div className="score-tile-bar"><div className="score-tile-fill" style={{ width: dashboardData?.kpis?.profileScore || "0%" }}></div></div>
                  <span className="score-tile-percentage">{dashboardData?.kpis?.profileScore || "0%"}</span>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* KPI CARDS */}
        <div className="td-kpi-grid">
          {mockKpis.map((kpi, index) => (
            <div className="td-kpi-card" key={index}>
              <div className="kpi-top">
                <div className="kpi-icon-box">{kpi.icon}</div>
                <div className={`kpi-trend ${kpi.isPositive ? 'positive' : 'negative'}`}>{kpi.isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}<span>{kpi.trend}</span></div>
              </div>
              <h3 className="kpi-value">{kpi.value}</h3>
              <p className="kpi-label">{kpi.title}</p>
            </div>
          ))}
        </div>

        {/* MIDDLE SECTION */}
        <div className="td-middle-grid">
          <div className="td-card td-chart-card">
            <div className="cdd-card-header"><h2>Audition Overview</h2></div>
            <div className="chart-container pie-chart-wrapper" style={{ position: "relative", width: "100%", height: "220px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0];
                      return (<div className="custom-pie-tooltip"><div className="tooltip-header"><span className="tooltip-dot" style={{ backgroundColor: COLOR_MAP[data.name] || "#3b82f6" }}></span><p className="tooltip-title">{data.name}</p></div><p className="tooltip-value">Count: <span>{data.value}</span></p></div>);
                    }
                    return null;
                  }} />
                  <Pie data={pieChartData} cx="50%" cy="50%" innerRadius={currentInnerRadius} outerRadius={currentOuterRadius} paddingAngle={4} dataKey="applications" cornerRadius={5} stroke="none">
                    {pieChartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLOR_MAP[entry.name] || "#3b82f6"} />))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="pie-center-text"><span className="pie-center-value">{totalAppliedCount}</span><span className="pie-center-label">Total</span></div>
            </div>
            <div className="td-chart-stats justify-content-center">
              <div className="td-chart-stat-item"><span className="td-chart-dot shortlisted"></span><p>Shortlisted</p></div>
              <div className="td-chart-stat-item"><span className="td-chart-dot pending"></span><p>Pending</p></div>
              <div className="td-chart-stat-item"><span className="td-chart-dot rejected"></span><p>Rejected</p></div>
            </div>
          </div>
          
          {/* RECOMMENDED SLIDER */}
          <div className="td-recommended-wrapper">
            <div className="card-header td-rec-header">
              <h2>Recommended for You</h2>
              {dashboardData?.recommendations?.length > 2 && (<button className="td-view-all-btn" onClick={() => navigate("/roles")}>View All</button>)}
            </div>
            <div className="td-recommended-slider">
              {dashboardData?.recommendations?.length > 0 ? (
                dashboardData.recommendations.map((rec, index) => (
                  <div className="td-recommended-slide" key={index}>
                    <div className="position-relative w-100 h-100">
                      <ModernAuditionCard audition={{ id: rec.id, jogetRoleId: rec.id, urlSlug: rec.urlSlug || rec.project, projectName: rec.project, roleTitle: rec.desc, roleCategory: rec.category, projectImageUrl: rec.img, auditionType: rec?.types?.[0] || "Online", projectEndDate: rec.endDate, projectStartDate: rec.startDate, roleCost: { totalAmount: rec.price?.replace("₹", "")?.trim() } }} />
                      {rec.isApplied && (<span className="position-absolute badge bg-success text-white fw-bold px-2 py-1 shadow" style={{ top: '12px', left: '12px', zIndex: 10, fontSize: '0.75rem', borderRadius: '4px' }}>✓ Applied</span>)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="w-100 text-center py-5 text-muted border border-secondary border-dashed rounded" style={{ borderStyle: 'dashed' }}>
                  <Info className="mb-2" size={24} /><p className="mb-0 small">No roles matching your profile gender/talent categories found yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BOTTOM SECTION: TABLE */}
        <div className="td-card td-table-card">
          <div className="card-header">
            <h2>Recent Applications</h2>
            <div className="table-filters">
              <button className={activeTab === "all" ? "active" : ""} onClick={() => setActiveTab("all")}>All</button>
              <button className={activeTab === "selected" ? "active" : ""} onClick={() => setActiveTab("selected")}>Shortlisted</button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="td-table">
              <thead>
                <tr><th>Project Name</th><th>Role Applied</th><th>Type</th><th>Applied On</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {dashboardData?.applications?.filter(row => activeTab === "all" ? true : activeTab === "selected" ? row.status?.toLowerCase() === "selected" : true)?.map(row => (
                  <tr key={row.id}>
                    <td><div className="table-project-cell"><div className="project-avatar">{row.project.charAt(0)}</div><span className="project-name">{row.project}</span></div></td>
                    <td className="table-text-muted">{row.role}</td>
                    <td><div className="table-type-cell">{row.type.includes("Online") ? <Video size={16}/> : <MapPin size={16}/>}<span>{row.type}</span></div></td>
                    <td className="table-text-muted">{row.date}</td>
                    <td>{getStatusBadge(row.status)}</td>
                    <td>
                      <button className="btn d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', borderRadius: '6px', padding: '0', backgroundColor: '#2563eb', border: 'none' }} title="View Details" onClick={() => navigate(`/audition/${row.id}/${row.urlSlug}`)}>
                        <Eye size={18} color="#ffffff" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* ✅ MODAL */}
      <Modal show={showFollowingModal} onHide={() => setShowFollowingModal(false)} centered dialogClassName="cd-following-modal-dialog" contentClassName="bg-dark text-white border border-secondary rounded-4 shadow-lg">
        <Modal.Header closeButton closeVariant="white" className="border-secondary px-3 py-2.5" style={{ background: '#111827' }}>
          <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2 text-white">
            <Users size={18} className="text-primary" /> Followed Casting Directors
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="cd-modal-body px-3 py-2.5" style={{ maxHeight: '420px', overflowY: 'auto', background: '#0b0f19' }}>
          {dashboardData?.followedCDs?.length > 0 ? (
            <div className="d-flex flex-column gap-2.5">
              {dashboardData.followedCDs.map((cd) => (
                <div key={cd.id} className="p-3 rounded-3 d-flex flex-column gap-3 cd-modal-card" style={{ background: '#141824', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}>
                  
                  {/* TOP ROW: AVATAR + NAME + BADGE + AGENCY + UNFOLLOW BUTTON */}
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center fw-bold bg-danger text-white overflow-hidden flex-shrink-0 cd-modal-avatar" 
                        style={{ width: '44px', height: '44px', border: '2px solid #E10600', fontSize: '0.95rem', cursor: 'pointer' }}
                        onClick={() => navigateToCDProfile(cd.id)}
                      >
                        {cd.img ? <img src={cd.img} alt={cd.name} className="w-100 h-100" style={{ objectFit: 'cover' }} /> : cd.name.charAt(0)}
                      </div>
                      
                      <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <h6 className="m-0 fw-bold text-white text-truncate cd-modal-name" style={{ fontSize: '0.95rem', cursor: 'pointer' }} onClick={() => navigateToCDProfile(cd.id)}>
                            {cd.name}
                          </h6>
                          {cd.specialization && (
                            <span className="px-2 py-0.5 rounded-pill fw-bold text-white d-inline-flex align-items-center flex-shrink-0 cd-modal-badge" style={{ background: '#E10600', fontSize: '0.65rem', letterSpacing: '0.3px' }}>
                              <Film size={10} className="me-1" color="#ffffff" /> {formatCamelCase(cd.specialization)}
                            </span>
                          )}
                        </div>
                        <small className="d-block text-truncate fw-medium text-secondary mt-0.5 cd-modal-agency" style={{ fontSize: '0.78rem' }}>
                          {cd.agency ? formatCamelCase(cd.agency) : 'Casting Agency'}
                        </small>
                      </div>
                    </div>

                    {/* COMPACT UNFOLLOW BUTTON */}
                    <button 
                      className="btn btn-danger btn-sm px-3 py-1 fw-bold rounded-pill shadow-sm flex-shrink-0 ms-auto cd-modal-unfollow-btn" 
                      style={{ background: '#E10600', color: '#ffffff', border: 'none', fontSize: '0.75rem', height: '28px', lineHeight: '1' }} 
                      onClick={() => handleModalFollowToggle(cd.id, cd.name)}
                    >
                      Unfollow
                    </button>
                  </div>

                  {/* BOTTOM ROW: 3 EQUAL STAT CARDS WITH CIRCULAR ICON BADGES */}
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    {/* CARD 1: ROLES */}
                    <div className="d-flex align-items-center gap-2 p-2 rounded-3 flex-grow-1 cd-stat-box" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', minHeight: '48px' }}>
                      <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 cd-stat-icon-circle" style={{ width: '32px', height: '32px', border: '1.5px solid #0284c7', background: 'rgba(56, 189, 248, 0.1)' }}>
                        <Briefcase size={14} color="#38bdf8" />
                      </div>
                      <div>
                        <div className="fw-bold text-info cd-stat-num" style={{ fontSize: '0.88rem', lineHeight: '1' }}>{cd.totalRoles || 0}</div>
                        <div className="text-secondary cd-stat-lbl" style={{ fontSize: '0.68rem', fontWeight: '500', marginTop: '2px' }}>Roles</div>
                      </div>
                    </div>

                    {/* CARD 2: PROJECTS */}
                    <div className="d-flex align-items-center gap-2 p-2 rounded-3 flex-grow-1 cd-stat-box" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', minHeight: '48px' }}>
                      <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 cd-stat-icon-circle" style={{ width: '32px', height: '32px', border: '1.5px solid #ca8a04', background: 'rgba(250, 204, 21, 0.1)' }}>
                        <Film size={14} color="#facc15" />
                      </div>
                      <div>
                        <div className="fw-bold text-warning cd-stat-num" style={{ fontSize: '0.88rem', lineHeight: '1' }}>{cd.totalProjects || 0}</div>
                        <div className="text-secondary cd-stat-lbl" style={{ fontSize: '0.68rem', fontWeight: '500', marginTop: '2px' }}>Projects</div>
                      </div>
                    </div>

                    {/* CARD 3: SUCCESS */}
                    <div className="d-flex align-items-center gap-2 p-2 rounded-3 flex-grow-1 cd-stat-box" style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.3)', minHeight: '48px' }}>
                      <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 cd-stat-icon-circle" style={{ width: '32px', height: '32px', border: '1.5px solid #059669', background: 'rgba(16, 185, 129, 0.15)' }}>
                        <TrendingUp size={14} color="#10b981" />
                      </div>
                      <div>
                        <div className="fw-bold text-success cd-stat-num" style={{ fontSize: '0.88rem', lineHeight: '1' }}>{cd.successRate || '94%'}</div>
                        <div className="cd-stat-lbl" style={{ fontSize: '0.68rem', fontWeight: '500', color: '#9ca3af', marginTop: '2px' }}>Success</div>
                      </div>
                    </div>
                  </div>

                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              <Users size={28} className="mb-2 opacity-50" />
              <p className="mb-0 small">You are not following any Casting Directors yet.</p>
            </div>
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default TalentDashboard;